import { OPMCalculatorResultsResponse } from 'api';
import { largeCurrencyFormat, sharesFormat, smallCurrencyFormat } from 'common/formats/formats';
import { REGULAR_UNIT } from 'components/FeaturedSpreadsheet/constants';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import { alphabetGenerator } from 'utilities/alphabet-utilities';
import {
  getOPMCalculatorSecurityWorkbookKey,
  HEADER_ROW_CLASS_NAME,
  OPM_CALCULATOR_SECURITY_COLUMN_HEADERS,
  SECURITY_TITLE,
  TOTAL_ROW_TITLE,
} from '../constants';

interface CreateSecurityConfigParams {
  panelId: string;
  results: OPMCalculatorResultsResponse;
}

const HEADER_ROW = 1;

/** Content-column indexes; the row titles are rendered in the spreadsheet's titles column. */
const COLUMN = {
  TOTAL_ALLOCATION: 0,
  SHARES_OUTSTANDING: 1,
  VALUE_PER_SHARE: 2,
};

/**
 * Final per-security breakdown: Total Allocation, Shares Outstanding, Value per Share.
 *
 * Every security on the cap table appears -- there is no fund-ownership filtering on this page.
 * Total Allocation and Shares Outstanding are summed; Value per Share is not, because a sum of
 * per-share prices is meaningless.
 */
const createSecurityConfig = ({ panelId, results }: CreateSecurityConfigParams) => {
  const { securities } = results;
  const alphabet = alphabetGenerator([], OPM_CALCULATOR_SECURITY_COLUMN_HEADERS.length);

  const rowConfig = [
    {
      value: SECURITY_TITLE,
      className: HEADER_ROW_CLASS_NAME,
      gridType: 'string',
      ignoreRowCopy: true,
      largeHeader: true,
      readOnly: true,
    },
    ...securities.map(security => ({
      value: security.name,
      gridType: 'string',
      ignoreRowCopy: true,
      readOnly: true,
    })),
    {
      value: TOTAL_ROW_TITLE,
      gridType: 'string',
      ignoreRowCopy: true,
      readOnly: true,
      titleClassName: 'subtitle',
    },
  ];

  // Securities start on row 2, below the header row.
  const securityRow = (index: number) => index + 2;
  const totalRow = securities.length + 2;

  const parser = () => {
    let cells: Record<string, unknown> = {};

    const readOnlyCell = (key: string, value: number, format: object, customFormat?: object) => ({
      key,
      value,
      format,
      customFormat,
      gridType: 'number',
      readOnly: true,
    });

    OPM_CALCULATOR_SECURITY_COLUMN_HEADERS.forEach((header, index) => {
      const key = `${alphabet[index]}${HEADER_ROW}`;
      cells[key] = { key, value: header, gridType: 'string', readOnly: true };
    });

    let totalAllocation = 0;
    let totalShares = 0;

    securities.forEach((security, index) => {
      const row = securityRow(index);
      totalAllocation += security.aggregate_value;
      totalShares += security.shares_outstanding;

      cells = {
        ...cells,
        [`${alphabet[COLUMN.TOTAL_ALLOCATION]}${row}`]: readOnlyCell(
          `${alphabet[COLUMN.TOTAL_ALLOCATION]}${row}`,
          security.aggregate_value,
          largeCurrencyFormat
        ),
        [`${alphabet[COLUMN.SHARES_OUTSTANDING]}${row}`]: readOnlyCell(
          `${alphabet[COLUMN.SHARES_OUTSTANDING]}${row}`,
          security.shares_outstanding,
          sharesFormat
        ),
        [`${alphabet[COLUMN.VALUE_PER_SHARE]}${row}`]: readOnlyCell(
          `${alphabet[COLUMN.VALUE_PER_SHARE]}${row}`,
          security.present_value,
          smallCurrencyFormat,
          // A per-share price follows the selected currency but never the thousands/millions scaler.
          { currency: { units: REGULAR_UNIT } }
        ),
      };
    });

    cells = {
      ...cells,
      [`${alphabet[COLUMN.TOTAL_ALLOCATION]}${totalRow}`]: readOnlyCell(
        `${alphabet[COLUMN.TOTAL_ALLOCATION]}${totalRow}`,
        totalAllocation,
        largeCurrencyFormat
      ),
      [`${alphabet[COLUMN.SHARES_OUTSTANDING]}${totalRow}`]: readOnlyCell(
        `${alphabet[COLUMN.SHARES_OUTSTANDING]}${totalRow}`,
        totalShares,
        sharesFormat
      ),
      // Deliberately blank: summing per-share prices would be meaningless.
      [`${alphabet[COLUMN.VALUE_PER_SHARE]}${totalRow}`]: {
        key: `${alphabet[COLUMN.VALUE_PER_SHARE]}${totalRow}`,
        value: '',
        gridType: 'string',
        readOnly: true,
      },
    };

    return cells;
  };

  return new SpreadsheetConfig({
    name: getOPMCalculatorSecurityWorkbookKey(panelId),
    parser,
    rowConfig,
    columns: OPM_CALCULATOR_SECURITY_COLUMN_HEADERS.map(() => ({})),
    tableData: { results },
    tableTerms: {
      tableName: SECURITY_TITLE,
      tableSlug: 'opm-calculator-security',
    },
    showToolbar: false,
    showTotalColumn: false,
    currencyFormatter: false,
    unitsFormatter: false,
    allowCopyColumn: false,
    allowReorderColumns: false,
    allowConfirmAndDeleteColumn: false,
  } as unknown as ConstructorParameters<typeof SpreadsheetConfig>[0]);
};

export default createSecurityConfig;
